/**
 * «کلینیک من» — the vocabulary and derivations both surfaces share.
 *
 * The desktop workbench and the mobile reduction render differently by doctrine
 * (clinic-app/CLAUDE.md §4.1: `.web.jsx` is canonical, `.mobile.jsx` is a
 * reduction of it). What they must never do is disagree about which view is
 * active, who may press what, or what still needs setting up — so the registry,
 * the rights derivation and the checklist live here and are imported by both.
 *
 * Nothing in this file renders. No React, no JSX, no tokens.
 */

import { isBlocked } from '../../hooks/useSubscription';

// ── The four views ───────────────────────────────────────────────────────────
//
// Four facets of ONE subject — who the clinic is, how people reach it, which
// WhatsApp account it speaks to them from, where it stands with Parsina. They
// are the same KIND of thing, which is why this is a tab strip and not the
// `ObjectRail` that «عملیات و هزینه» uses: a rail earns its width by saying two
// groups of entries are different kinds of work.
//
// `legacyPath` is the route this view absorbed. It is read by nothing at
// runtime — the redirects live in App.jsx, where the router can see them — but
// it is the record of which bookmark lands where, next to the view it lands on.

export const CLINIC_VIEWS = (t) => ([
  { key: 'profile', label: t('clinic.views.profile'), legacyPath: null },
  { key: 'connect', label: t('clinic.views.connect'), legacyPath: '/about' },
  // «اپلیکیشن بیمار» — the rules the patient app plays by (booking requests
  // today, document uploads next). It sits between «اتصال بیماران», which is how
  // a patient GETS the app, and «واتس‌اپ», which is the other outbound channel:
  // the three are how the clinic reaches the people it treats.
  { key: 'patientApp', label: t('clinic.views.patientApp'), legacyPath: null },
  { key: 'whatsapp', label: t('clinic.views.whatsapp'), legacyPath: null },
  // «قالب‌های پیام» — what the clinic SAYS to patients, next to the account it
  // says it from. It is the only place a message template can be written; the
  // «متن پیام» picker in «ارسال پیام» reads exactly this list.
  { key: 'templates', label: t('clinic.views.templates'), legacyPath: null },
  // «اعلان‌ها» — WHEN the clinic speaks, beside what it says and the account it says it
  // from. The defaults «ثبت نوبت» ticks used to be a section of «تنظیمات», which is
  // deprecated and which the desktop panel never linked to at all.
  { key: 'notifications', label: t('clinic.views.notifications'), legacyPath: '/settings?section=notifications' },
  // «پیش‌پرداخت» (PM-0657 §6.6) — the clinic's rule for money taken at «ثبت نوبت».
  { key: 'prepayment', label: t('clinic.views.prepayment'), legacyPath: null },
  { key: 'billing', label: t('clinic.views.billing'), legacyPath: '/subscription' },
  // «انبار» — one control (the depth) rather than a settings page. It lives with
  // the clinic's own shape because it changes what every other screen shows, not
  // because it is a warehouse operation.
  { key: 'inventory', label: t('clinic.views.inventory'), legacyPath: null },
]);

/**
 * View keys, structure only.
 *
 * Read through a translator that returns the key it is handed: this is an answer
 * about the URL, and a URL has no language. Threading a real `t` in would turn a
 * module constant into a function and every reader into a call, for labels it
 * never looks at.
 */
export const VIEW_KEYS = CLINIC_VIEWS((key) => key).map((v) => v.key);

export const DEFAULT_VIEW = 'profile';

/**
 * Which view a URL asks for.
 *
 * A gateway return wins over `?view=`, unconditionally. The payment BFF appends
 * `payment=success|failed` to whatever return URL the checkout supplied, and the
 * legacy Zarinpal leg appends `Authority`/`Status`; if either is present the
 * person has just come back from paying, and the only view that can report that
 * is billing. Trusting `?view=` there would show a clinic that has just paid its
 * profile form, with the payment result silently stripped by the URL cleanup.
 */
export function resolveView(param, search = '') {
  const params = new URLSearchParams(search || '');
  if (params.get('payment') || params.get('Authority') || params.get('authority')) {
    return 'billing';
  }
  return VIEW_KEYS.includes(param) ? param : DEFAULT_VIEW;
}

// ── Who may do what ──────────────────────────────────────────────────────────

/**
 * The two authorization axes, resolved once for the whole page.
 *
 * RBAC answers "may this PERSON?" and entitlement answers "may this CLINIC right
 * now?" — the BFF requires both and reports them with different error codes, so
 * the UI must keep them apart too. A control the person lacks the permission for
 * is not rendered as an action at all; a control the clinic's subscription
 * blocks is rendered disabled WITH the reason and a way to fix it, because that
 * one has a fix and it is on this very page.
 *
 * Both are UX only. The BFF re-checks every mutation, so a tampered client wins
 * nothing.
 *
 * `updateClinic` is deliberately absent from the entitlement side: a clinic must
 * always be able to correct its own phone number, even unpaid. `generateInvitationCode`
 * is gated because recruiting new people is growth. One permission, two answers —
 * which is exactly why `entitlementMap.js` keys on the GraphQL field.
 */
export function clinicRights(access, capabilities, { isOwner = false } = {}) {
  const canSettings = !!access?.can?.('clinic.settings.manage');
  const codeBlocked = isBlocked(capabilities, 'generateInvitationCode');
  return {
    canEditProfile: canSettings,
    /**
     * «نوع کلینیک» — one tier above the rest of the profile.
     *
     * Editing the clinic's details describes it to a patient; changing its TYPE
     * decides which slice of the service catalogue every «افزودن خدمت» picker in
     * the clinic leads with. A receptionist who may fix the address should not
     * be able to turn the clinic dental in passing, so this needs the settings
     * permission AND the owner/admin tier — the same pair the BFF enforces,
     * which strips the field rather than failing the save for anyone below it.
     */
    canChangeType: canSettings && !!isOwner,
    /**
     * «واحد پول» — the same pair as the type, and the BFF refuses anyone else
     * (docs/clinic-currency/CONTRACTS.md §7). Saved on its own, never through
     * the profile's full-replace `updateClinic`.
     */
    canChangeCurrency: canSettings && !!isOwner,
    canGenerateCode: canSettings && !codeBlocked,
    /** Has the permission, but the subscription is in the way — the actionable case. */
    codeBlockedByEntitlement: canSettings && codeBlocked,
    canManageBilling: !!access?.can?.('clinic.billing.manage'),
    /*
     * Linking a WhatsApp account is a clinic-settings act, not a per-message
     * one — the same key the BFF's pairing route checks for itself. Named
     * separately from `canEditProfile` because they only happen to coincide.
     */
    canManageWhatsapp: canSettings,
    /*
     * «اپلیکیشن بیمار» — the same key, for the same reason as WhatsApp: deciding
     * whether patients may ask for appointments, and how far ahead, is a
     * clinic-settings act rather than a reception one. Named separately because
     * the two only happen to coincide today, and the BFF gates the policy
     * mutation on `clinic.settings.manage` for both read and write.
     */
    canManagePatientApp: canSettings,
    /*
     * The inventory's DEPTH is a clinic-shape decision, so changing it rides
     * `canEditProfile` like the rest of this screen. Seeing the tab is a weaker
     * question — a مسئول انبار should be able to read which level the clinic is
     * at without being able to move it.
     */
    canSeeInventory: !!access?.can?.('inventory.view'),
    /*
     * A template is a clinic-wide asset: editing one changes what EVERYONE at the
     * desk sends, which is authority over the clinic's voice rather than over a
     * single message. So it rides the settings key, not `patients.manage` — the
     * same split the BFF's permission map makes (reads ride `patients.view`, so
     * the compose dropdown still loads for a receptionist who may not edit it).
     */
    canManageTemplates: canSettings,
    /*
     * «اعلان‌ها» — the same settings key, for the same reason: which channels every
     * booking announces itself on spends the booker's own پارسه and changes what the whole
     * desk sends. The BFF gates the save on `clinic.settings.manage` and lets anyone who
     * may BOOK read the defaults, which is what pre-ticks the boxes in «ثبت نوبت».
     */
    canManageNotifications: canSettings,
    /*
     * «پیش‌پرداخت» (PM-0657 §6.6) — `updatePrepaymentPolicy` is gated on
     * `clinic.settings.manage`; the desk READS the policy through its own query
     * wherever it books, so the tab is the settings key's alone.
     */
    canManagePrepayment: canSettings,
  };
}

/**
 * The tabs a given person actually gets.
 *
 * WhatsApp is the first view that not everyone may see: a receptionist may read
 * the clinic's profile but may not link the account it messages from, and a tab
 * that opens onto a panel whose every button is refused is worse than no tab.
 */
export function visibleViews(t, rights) {
  return CLINIC_VIEWS(t).filter((v) => {
    if (v.key === 'whatsapp') return !!rights?.canManageWhatsapp;
    // The BFF refuses `clinicSettings` without `clinic.settings.manage`, so a tab
    // shown to a receptionist would open onto an error, not a read-only card.
    if (v.key === 'patientApp') return !!rights?.canManagePatientApp;
    // Reading the inventory's depth is «مشاهدهٔ انبار»; changing it is the
    // clinic's own settings right, which the panel itself enforces.
    if (v.key === 'inventory') return !!rights?.canSeeInventory;
    // Hidden rather than disabled, like «واتس‌اپ»: a tab onto a panel whose every
    // button is refused is worse than no tab. A receptionist still READS
    // templates — in the compose dropdown, where they are of use.
    if (v.key === 'templates') return !!rights?.canManageTemplates;
    // Same as «قالب‌های پیام»: every control on the panel is a settings act, and the
    // defaults a receptionist needs are already ticked for them in «ثبت نوبت».
    if (v.key === 'notifications') return !!rights?.canManageNotifications;
    if (v.key === 'prepayment') return !!rights?.canManagePrepayment;
    return true;
  });
}

// ── Clinic identity ──────────────────────────────────────────────────────────

/**
 * Clinic type key → label, in the reading locale.
 *
 * This is a TRANSLATION TABLE, not the vocabulary. The vocabulary lives in the
 * `clinic_types` registry in the clinicmanagement service, is served by the
 * `clinicTypes` query, and is what `clinicmanagement` validates a write against —
 * see `hooks/useClinicTypes.js`. What lives here is the Persian/English/Arabic
 * wording for the keys this build knows, because the registry ships only two
 * languages and the panel runs in three.
 *
 * A key the registry has but this table does not still renders: `labelFor` falls
 * back to the server's own Persian label, then to the key. So adding a clinic
 * type is a server-side change, and translating it is a follow-up rather than a
 * prerequisite.
 */
export const CLINIC_TYPE_LABELS = (t) => ({
  general: t('clinic.types.general'),
  specialty: t('clinic.types.specialty'),
  dental: t('clinic.types.dental'),
  ophthalmology: t('clinic.types.ophthalmology'),
  dermatology: t('clinic.types.dermatology'),
  pediatric: t('clinic.types.pediatric'),
  cardiology: t('clinic.types.cardiology'),
  orthopedic: t('clinic.types.orthopedic'),
  neurology: t('clinic.types.neurology'),
  gynecology: t('clinic.types.gynecology'),
  internal: t('clinic.types.internal'),
  ent: t('clinic.types.ent'),
  urology: t('clinic.types.urology'),
  psychology: t('clinic.types.psychology'),
  aesthetic: t('clinic.types.aesthetic'),
  physiotherapy: t('clinic.types.physiotherapy'),
  rehabilitation: t('clinic.types.rehabilitation'),
  laboratory: t('clinic.types.laboratory'),
  radiology: t('clinic.types.radiology'),
  pharmacy: t('clinic.types.pharmacy'),
});

/** The three buckets the type picker groups its options into. */
export const TYPE_GROUP_LABELS = (t) => ({
  general: t('clinic.typeGroups.general'),
  specialty: t('clinic.typeGroups.specialty'),
  paraclinic: t('clinic.typeGroups.paraclinic'),
});

/**
 * Label for a clinic type, falling back to the raw code before the generic word.
 *
 * The `LEGACY_TYPE_ALIASES` map that used to sit here is gone: the four uppercase
 * values (CLINIC / POLYCLINIC / HOSPITAL / SPECIALTY) are rewritten by migration
 * a4f1c8e37b52, and until that has run everywhere the clinic BFF folds them on
 * the READ path (`canonicalClinicType` in clinicManagementClient.js). Two
 * fallbacks for one legacy shape is one too many — and the client's copy was the
 * one that could not also fix what gets SENT to the catalogue filter.
 */
export function clinicTypeLabel(type, t) {
  if (!type) return t('clinic.types.clinic');
  return CLINIC_TYPE_LABELS(t)[type] || type;
}

/**
 * The type a clinic is registered as when nobody chooses.
 *
 * Mirrors DEFAULT_CLINIC_TYPE in the clinicmanagement registry. It is here so the
 * wizard can pre-select something before the `clinicTypes` query has answered;
 * the server applies the same default for a create that names none.
 */
export const DEFAULT_CLINIC_TYPE = 'general';

/** «کلینیک نور · تخصصی · تهران» — the page header's subtitle. */
export function clinicSummary(clinic, t) {
  if (!clinic) return t('clinic.subtitle');
  return [clinic.name, clinicTypeLabel(clinic.clinicType, t), clinic.city]
    .filter(Boolean)
    .join(' · ');
}

/** The patient-facing join URL for an invitation code, or null when there is none. */
export function joinUrl(code, patientOrigin) {
  if (!code || !patientOrigin) return null;
  return `${patientOrigin}/join/${code}`;
}

// ── Writing a clinic back ────────────────────────────────────────────────────

/**
 * The editable profile fields, in the order they are asked for.
 * One list, so the form, the dirty check and the read-only view cannot drift.
 *
 * `latitude`/`longitude` joined it when the location picker shipped. They are
 * split out because they are NUMBERS and everything else here is a string: an
 * empty text field is `''` and an unset coordinate is `null`, and folding the
 * two together makes `0` — a real position in the Gulf of Guinea — look like
 * "not set".
 */
export const PROFILE_TEXT_FIELDS = [
  'name', 'province', 'city', 'address', 'officePhone',
  // The kind of clinic this is. A text field like the others — a registry key,
  // never a label — and it joins them for the same reason they are listed here:
  // the form, the dirty check and the read-only view must not drift.
  //
  // Whether it may be CHANGED is a separate question, asked one tier up
  // (owner/admin only); the BFF strips the field for anyone below it, so a
  // read-only render and a stripped write agree without this list knowing.
  'clinicType',
  // The clinic's JURISDICTION (PM-0082): an ISO 3166-1 code. Same shape and
  // same gate as the type — owner/admin, stripped by the BFF for anyone else —
  // and the same "omitted = unchanged" on the wire (see `toUpdateInput`).
  'country',
];
export const PROFILE_COORD_FIELDS = ['latitude', 'longitude'];

/** A coordinate as it travels: a finite number, or `null` for "no position". */
export function toCoord(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Whether two coordinates are the same position.
 *
 * Not `===`. The value makes a round trip through JSON and a `double` column,
 * so a clinic that was never moved can come back as 35.689200000000004 and a
 * strict compare would leave the form permanently dirty — a «ذخیره نشده» bar
 * that never goes away, on a page nobody edited.
 */
export function coordsEqual(a, b) {
  const x = toCoord(a);
  const y = toCoord(b);
  if (x == null || y == null) return x === y;
  return Math.abs(x - y) < 1e-7;
}

/** A clinic record reduced to the draft the form edits. */
export function profileDraft(clinic) {
  const draft = {};
  for (const key of PROFILE_TEXT_FIELDS) draft[key] = clinic?.[key] || '';
  for (const key of PROFILE_COORD_FIELDS) draft[key] = toCoord(clinic?.[key]);
  return draft;
}

/** Whether a draft differs from the saved clinic. */
export function isProfileDirty(draft, clinic) {
  if (!draft) return false;
  const saved = profileDraft(clinic);
  return PROFILE_TEXT_FIELDS.some((key) => (draft[key] || '') !== saved[key])
    || PROFILE_COORD_FIELDS.some((key) => !coordsEqual(draft[key], saved[key]));
}

/**
 * Build the `UpdateClinicInput` for a save.
 *
 * **Always sends the complete record**, and that is load-bearing rather than
 * tidy. `clinicmanagement`'s `update_clinic` is a FULL REPLACE — it assigns
 * every column from the request and the BFF coerces a missing number to `0`,
 * which the server then reads back as `None`. So any field left out of this
 * object is not "unchanged", it is ERASED. The screen this replaced never sent
 * latitude/longitude, which means every clinic edit ever made through it
 * silently dropped the clinic's coordinates.
 *
 * `overrides` is how the status row flips `active` without owning the form: it
 * passes `{ active: false }` and the current values of everything else travel
 * with it automatically.
 */
export function toUpdateInput(clinic, draft = {}, overrides = {}) {
  const merged = { ...profileDraft(clinic), ...draft };
  return {
    name: (merged.name || '').trim(),
    province: (merged.province || '').trim() || null,
    city: (merged.city || '').trim() || null,
    address: (merged.address || '').trim() || null,
    officePhone: (merged.officePhone || '').trim() || null,
    // Editable since the location picker shipped, and still `null` rather than
    // `0` for "no coordinates" — `0,0` is a real position in the Gulf of Guinea.
    latitude: toCoord(merged.latitude),
    longitude: toCoord(merged.longitude),
    // The ONE field this full-replace does not full-replace. `UpdateClinicInput`
    // reads an omitted clinicType as "unchanged" all the way down to the
    // repository, so sending `null` for a clinic whose type could not be read
    // would be the one way to blank it. Undefined keeps it.
    clinicType: (merged.clinicType || '').trim() || undefined,
    // Likewise the country: omitted is UNCHANGED down to the repository, so a
    // clinic read from a BFF older than the field keeps its country.
    country: (merged.country || '').trim() || undefined,
    active: clinic?.active ?? true,
    ...overrides,
  };
}

// ── Setup checklist ──────────────────────────────────────────────────────────

/**
 * What a newly-created clinic still has to do.
 *
 * Four independent one-step tasks, not a wizard: a clinic can print its QR
 * before it has an address, and paying can wait until the trial is nearly over.
 * The card renders only while something is missing and disappears for good once
 * the list empties, so a finished clinic is never nagged.
 *
 * A subscription in TRIALING is deliberately NOT counted as done — the trial is
 * the reason the item is worth showing — but an unreachable subscription service
 * (`unavailable`, the BFF's fail-open) is skipped rather than reported as an
 * outstanding task the clinic cannot action.
 */
export function setupTasks(clinic, capabilities, t) {
  if (!clinic) return [];
  const tasks = [];
  if (!clinic.address) {
    tasks.push({ key: 'address', label: t('clinic.checklist.address'), view: 'profile', field: 'address' });
  }
  if (!clinic.officePhone) {
    tasks.push({ key: 'phone', label: t('clinic.checklist.phone'), view: 'profile', field: 'officePhone' });
  }
  if (!clinic.invitationCode) {
    tasks.push({ key: 'code', label: t('clinic.checklist.code'), view: 'connect' });
  }
  if (capabilities && !capabilities.unavailable && capabilities.status === 'TRIALING') {
    tasks.push({ key: 'subscription', label: t('clinic.checklist.subscription'), view: 'billing' });
  }
  return tasks;
}

// ── Near-expiry ──────────────────────────────────────────────────────────────

/** Days left at which a healthy, paying clinic starts being nudged on this page. */
export const NEAR_EXPIRY_DAYS = 7;

/**
 * Whether an ACTIVE clinic is close enough to expiry to warrant a page-local cue.
 *
 * Deliberately narrower than the shell banner's `shouldWarn`, which stays silent
 * for a paying clinic on purpose: a permanent nag trains people to ignore the
 * alert zone, and then it fails when a real safety banner appears there. This
 * only tints a KPI and adds one line beside the plans — visible where an owner
 * would look for it, invisible everywhere else.
 */
export function isNearExpiry(capabilities) {
  if (!capabilities || capabilities.unavailable) return false;
  if (capabilities.status !== 'ACTIVE') return false;
  const days = capabilities.daysRemaining;
  return days != null && days >= 0 && days <= NEAR_EXPIRY_DAYS;
}
